import React from 'react';
import axios from 'axios';
import { useDashboard } from '../contexts/DashboardContext';
import CouponsSettings from '../components/CouponsSettings';
import TagsSettings from '../components/TagsSettings';
import Avatar from '../components/common/Avatar';
import StripeIntegrationSection from '../components/settings/StripeIntegrationSection';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

const SettingsPage = () => {
  const {
    API, accountSettings, activeTab, aryeoToken, aryeoTokenInput, automatedTaskRules,
    deleteAryeoToken, deleteProduct, deleteTeamMember, deselectAllRecords,
    disconnectTeamMemberCalendar, dropboxSettings, editTeamMemberForm, editingTeamMember,
    existingTeamMemberAvatarUrl, hasAryeoToken, hasPermission, importAryeoData,
    importConnectionStatus, importPagination, importing, loadMyTeamMemberData, loadingPreview,
    openEditProduct, openEditTeamMember, permissions, previewAryeoData, previewData, products,
    saveAryeoToken, savingToken, selectAllRecords, selectedRecords, setAccountSettings,
    setAryeoTokenInput, setAutomatedTaskRules, setDropboxSettings, setEditTeamMemberForm,
    setEditingTaskRule, setEditingTeamMember, setNewTaskRule, setSelectedMemberForCalendar,
    setSettingsTab, setShowAddProduct, setShowAddTaskRule, setShowAddTeamMember,
    setShowCalendarModal, setShowTokenInput, setTeamMemberAvatarFile, setTeamMemberAvatarPreview,
    setWorkingHours, settingsTab, showRecordsList, showToast, showTokenInput, teamMemberAvatarFile,
    teamMemberAvatarPreview, teamMemberAvatarUrls, testAryeoConnection, testingConnection,
    toggleRecordSelection, updateAccountSettings, updateWorkingHours, workingHours,
    teamMembers, clients, appointments, tasks,
  } = useDashboard();

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-6 md:px-8 lg:px-12">
      <h2 className="text-2xl font-bold" >Settings</h2>
      
      {/* Settings Navigation - Different tabs based on permissions */}
      <div className="flex space-x-2 mb-6">
        {(hasPermission('settings', 'manage_business') 
          ? [
              { key: 'account', label: 'Account' },
              { key: 'hours', label: 'Working Hours' },
              { key: 'scheduling', label: 'Scheduling' },
              { key: 'tasks', label: 'Tasks' },
              { key: 'coupons', label: 'Coupons' },
              { key: 'tags', label: 'Tags' },
              { key: 'integrations', label: 'Integrations' },
              { key: 'import', label: 'Import' }
            ]
          : [
              { key: 'account', label: 'Account Details' },
              { key: 'work-hours', label: 'Work Hours' },
              { key: 'scheduling', label: 'Scheduling Settings' },
              { key: 'calendar', label: 'Calendar' }
            ]
        ).map((tab) => (
          <button
            key={tab.key}
            onClick={() => setSettingsTab(tab.key)}
            className={`px-4 py-2 rounded-lg transition-all duration-200 ${
              settingsTab === tab.key ? 'bg-gray-100 font-semibold' : 'hover:bg-gray-300'
            }`}
            style={{
              color: settingsTab === tab.key ? 'var(--stripe-primary)' : 'var(--stripe-text-secondary)'
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>


      {/* Photographer Settings */}
      {!hasPermission('settings', 'manage_business') && (
        <>
          {/* Account Details Tab for Photographer */}
          {settingsTab === 'account' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gray-700">
                <h3 className="text-lg font-semibold text-white">Account Details</h3>
              </div>
              <div className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Avatar */}
                  <div className="md:col-span-2 flex items-center gap-6">
                    <div>
                      {existingTeamMemberAvatarUrl || teamMemberAvatarPreview ? (
                        <img 
                          src={teamMemberAvatarPreview || existingTeamMemberAvatarUrl}
                          alt={editTeamMemberForm.name}
                          className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
                        />
                      ) : (
                        <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center text-blue-400 font-semibold text-2xl border-4 border-gray-200">
                          {editTeamMemberForm.name ? editTeamMemberForm.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : '?'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Profile Picture</label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {
                          const file = e.target.files[0];
                          if (file) {
                            setTeamMemberAvatarFile(file);
                            const reader = new FileReader();
                            reader.onloadend = () => setTeamMemberAvatarPreview(reader.result);
                            reader.readAsDataURL(file);
                          }
                        }}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                      />
                    </div>
                  </div>

                  {/* Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                    <input
                      type="text"
                      value={editTeamMemberForm.name || ''}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, name: e.target.value})}
                      className="clara-input w-full"
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      value={editTeamMemberForm.email || ''}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, email: e.target.value})}
                      className="clara-input w-full"
                      placeholder="Enter your email"
                    />
                  </div>

                  {/* Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={editTeamMemberForm.phone || ''}
                      onChange={(e) => setEditTeamMemberForm({...editTeamMemberForm, phone: e.target.value})}
                      className="clara-input w-full"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  {/* Role (read-only) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                    <input
                      type="text"
                      value={editTeamMemberForm.role || ''}
                      className="clara-input w-full bg-gray-100"
                      disabled
                    />
                  </div>
                </div>

                <div className="mt-6 flex gap-3">
                  <button
                    onClick={async () => {
                      try {
                        // Upload avatar if there's a new file
                        if (teamMemberAvatarFile) {
                          const avatarFormData = new FormData();
                          avatarFormData.append('avatar', teamMemberAvatarFile);
                          await axios.post(`${API}/me/avatar`, avatarFormData);
                        }

                        // Update basic info using photographer-specific endpoint
                        await axios.put(`${API}/me/profile`, {
                          name: editTeamMemberForm.name,
                          email: editTeamMemberForm.email,
                          phone: editTeamMemberForm.phone
                        });

                        alert('Account details updated successfully!');
                        // Reload data
                        await loadMyTeamMemberData();
                        setTeamMemberAvatarFile(null);
                        setTeamMemberAvatarPreview(null);
                      } catch (error) {
                        alert('Failed to update account details: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="clara-btn-primary px-6 py-2"
                  >
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Work Hours Tab */}
          {settingsTab === 'work-hours' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gray-700">
                <h3 className="text-lg font-semibold text-white">Work Hours</h3>
              </div>
              <div className="p-8">
                <p className="text-gray-600 mb-6">Set your available work hours for scheduling appointments.</p>
                <div className="space-y-4">
                  {Object.keys(editTeamMemberForm?.working_hours || {}).map((day) => (
                    <div key={day} className="flex items-center gap-4 p-4 border border-gray-200 rounded-lg">
                      <div className="w-32">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={editTeamMemberForm.working_hours[day]?.enabled}
                            onChange={(e) => {
                              setEditTeamMemberForm({
                                ...editTeamMemberForm,
                                working_hours: {
                                  ...editTeamMemberForm.working_hours,
                                  [day]: {
                                    ...editTeamMemberForm.working_hours[day],
                                    enabled: e.target.checked
                                  }
                                }
                              });
                            }}
                            className="rounded"
                          />
                          <span className="font-medium capitalize">{day}</span>
                        </label>
                      </div>
                      {editTeamMemberForm.working_hours[day]?.enabled && (
                        <>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">Start Time</label>
                            <input
                              type="time"
                              value={editTeamMemberForm.working_hours[day]?.start || '09:00'}
                              onChange={(e) => {
                                setEditTeamMemberForm({
                                  ...editTeamMemberForm,
                                  working_hours: {
                                    ...editTeamMemberForm.working_hours,
                                    [day]: {
                                      ...editTeamMemberForm.working_hours[day],
                                      start: e.target.value
                                    }
                                  }
                                });
                              }}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-gray-600 mb-1">End Time</label>
                            <input
                              type="time"
                              value={editTeamMemberForm.working_hours[day]?.end || '17:00'}
                              onChange={(e) => {
                                setEditTeamMemberForm({
                                  ...editTeamMemberForm,
                                  working_hours: {
                                    ...editTeamMemberForm.working_hours,
                                    [day]: {
                                      ...editTeamMemberForm.working_hours[day],
                                      end: e.target.value
                                    }
                                  }
                                });
                              }}
                              className="border border-gray-300 rounded px-3 py-2"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                <div className="mt-6">
                  <button
                    onClick={async () => {
                      // Save work hours via API
                      try {
                        await axios.put(`${API}/me/work-hours`, {
                          working_hours: editTeamMemberForm.working_hours
                        });
                        alert('Work hours updated successfully!');
                        // Reload data to confirm
                        await loadMyTeamMemberData();
                      } catch (error) {
                        alert('Failed to update work hours: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    className="clara-btn-primary px-6 py-2"
                  >
                    Save Work Hours
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Scheduling Settings Tab */}
          {settingsTab === 'scheduling' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gray-700">
                <h3 className="text-lg font-semibold text-white">Scheduling Settings</h3>
              </div>
              <div className="p-6 space-y-6">
                {/* Client Reschedule/Cancel Settings */}
                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-1">Client Self-Service</h4>
                  <p className="text-sm text-gray-500 mb-4">Control when clients can reschedule or cancel appointments from their portal.</p>
                  
                  <div className="space-y-4">
                    {/* Lockout Hours */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1.5">
                        Reschedule/Cancel Lockout Window
                      </label>
                      <div className="flex items-center gap-3">
                        <input
                          type="number"
                          min="0"
                          max="168"
                          value={accountSettings.reschedule_cancel_lockout_hours || 24}
                          onChange={(e) => setAccountSettings(prev => ({
                            ...prev,
                            reschedule_cancel_lockout_hours: parseInt(e.target.value) || 0
                          }))}
                          className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                        <span className="text-gray-600">hours before appointment</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Clients won't be able to reschedule or cancel within this window. Set to 0 to always allow changes.
                      </p>
                    </div>
                    
                    {/* Allow Reschedule Toggle */}
                    <div className="flex items-center justify-between py-3 border-t border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">Allow Client Rescheduling</p>
                        <p className="text-sm text-gray-500">Clients can change their appointment date/time</p>
                      </div>
                      <button
                        onClick={() => setAccountSettings(prev => ({
                          ...prev,
                          allow_client_reschedule: !(prev.allow_client_reschedule !== false)
                        }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          accountSettings.allow_client_reschedule !== false ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          accountSettings.allow_client_reschedule !== false ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                    
                    {/* Allow Cancel Toggle */}
                    <div className="flex items-center justify-between py-3 border-t border-gray-100">
                      <div>
                        <p className="font-medium text-gray-900">Allow Client Cancellation</p>
                        <p className="text-sm text-gray-500">Clients can cancel their appointments</p>
                      </div>
                      <button
                        onClick={() => setAccountSettings(prev => ({
                          ...prev,
                          allow_client_cancel: !(prev.allow_client_cancel !== false)
                        }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          accountSettings.allow_client_cancel !== false ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          accountSettings.allow_client_cancel !== false ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                  </div>
                  
                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/settings/account`, {
                            ...accountSettings,
                            reschedule_cancel_lockout_hours: accountSettings.reschedule_cancel_lockout_hours || 24,
                            allow_client_reschedule: accountSettings.allow_client_reschedule !== false,
                            allow_client_cancel: accountSettings.allow_client_cancel !== false
                          });
                          alert('Scheduling settings saved!');
                        } catch (error) {
                          alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="clara-btn-primary px-6 py-2"
                    >
                      Save Settings
                    </button>
                  </div>
                </div>
                
                {/* Booking Review Settings */}
                <div className="border border-gray-200 rounded-lg p-5">
                  <h4 className="font-semibold text-gray-900 mb-1">Booking Submissions</h4>
                  <p className="text-sm text-gray-500 mb-4">Control how new bookings from your public booking page are handled.</p>
                  
                  <div className="space-y-4">
                    {/* Auto-Book Toggle */}
                    <div className="flex items-center justify-between py-3">
                      <div>
                        <p className="font-medium text-gray-900">Auto-Confirm Bookings</p>
                        <p className="text-sm text-gray-500">When enabled, bookings are confirmed automatically. When disabled, they go to a review queue.</p>
                      </div>
                      <button
                        onClick={() => setAccountSettings(prev => ({
                          ...prev,
                          order_form_auto_book: !prev.order_form_auto_book
                        }))}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          accountSettings.order_form_auto_book ? 'bg-blue-600' : 'bg-gray-300'
                        }`}
                        data-testid="auto-book-toggle"
                      >
                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          accountSettings.order_form_auto_book ? 'translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </div>
                    
                    {!accountSettings.order_form_auto_book && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                        <strong>Review Queue Mode:</strong> New bookings will appear at the top of the Appointments page for manual approval.
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-5 pt-4 border-t border-gray-100">
                    <button
                      onClick={async () => {
                        try {
                          await axios.put(`${API}/settings/account`, {
                            ...accountSettings,
                            order_form_auto_book: accountSettings.order_form_auto_book || false
                          });
                          alert('Booking settings saved!');
                        } catch (error) {
                          alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                        }
                      }}
                      className="clara-btn-primary px-6 py-2"
                      data-testid="save-order-form-settings"
                    >
                      Save Booking Settings
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Calendar Tab */}
          {settingsTab === 'calendar' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="px-6 py-4 bg-gray-700">
                <h3 className="text-lg font-semibold text-white">Google Calendar Integration</h3>
              </div>
              <div className="p-8">
                <p className="text-gray-600 mb-6">
                  Connect your Google Calendar to sync your availability and appointments.
                </p>
                {editingTeamMember && editingTeamMember.calendar_integration && editingTeamMember.calendar_integration.is_active ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-green-900">Connected</p>
                          <p className="text-sm text-green-700">
                            {editingTeamMember.calendar_integration.connected_email || 'Google Calendar'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => disconnectTeamMemberCalendar(editingTeamMember.id)}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium"
                      >
                        Disconnect
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">Not Connected</p>
                          <p className="text-sm text-gray-600">Connect to sync availability and events</p>
                        </div>
                      </div>
                      <button
                        onClick={async () => {
                          // Load data if not already loaded
                          if (!editingTeamMember) {
                            try {
                              const response = await axios.get(`${API}/me/team-member`);
                              setEditingTeamMember(response.data);
                              setSelectedMemberForCalendar(response.data);
                              setShowCalendarModal(true);
                            } catch (error) {
                              console.error('Failed to load team member data:', error);
                              alert('Failed to load your data. Please try again.');
                            }
                          } else {
                            setSelectedMemberForCalendar(editingTeamMember);
                            setShowCalendarModal(true);
                          }
                        }}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium"
                      >
                        Connect Calendar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}


      {/* Admin-Only Account Settings */}
      {hasPermission('settings', 'manage_business') && settingsTab === 'account' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700">
            <h3 className="text-lg font-semibold text-white">Account Information</h3>
          </div>
          <div className="p-8">
          {/* Business Logo */}
          <div className="mb-6 pb-6 border-b border-gray-200">
            <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Business Logo</label>
            <div className="flex items-center gap-4">
              {accountSettings.logo_url ? (
                <img src={accountSettings.logo_url.startsWith('http') ? accountSettings.logo_url : `${API.replace('/api', '')}${accountSettings.logo_url}`} alt="Logo" className="h-16 w-auto object-contain rounded border border-gray-200" />
              ) : (
                <div className="h-16 w-24 bg-gray-100 rounded border border-gray-200 flex items-center justify-center text-gray-400 text-xs">
                  No logo
                </div>
              )}
              <div className="flex gap-2">
                <label className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  {accountSettings.logo_url ? 'Change Logo' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    key={accountSettings.logo_url || 'no-logo'}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      
                      // Validate file size (5MB max)
                      if (file.size > 5 * 1024 * 1024) {
                        showToast('File too large. Maximum size: 5MB', 'error');
                        return;
                      }
                      
                      try {
                        const formData = new FormData();
                        formData.append('file', file);
                        const res = await axios.post(`${API}/settings/upload-logo`, formData);
                        if (res.data?.url) {
                          await axios.put(`${API}/settings/account`, { logo_url: res.data.url });
                          setAccountSettings(prev => ({ ...prev, logo_url: res.data.url }));
                          showToast('Logo uploaded!', 'success');
                        }
                      } catch (err) {
                        console.error('Logo upload error:', err);
                        showToast(err.response?.data?.detail || 'Failed to upload logo', 'error');
                      }
                      // Reset input to allow re-selecting same file
                      e.target.value = '';
                    }}
                  />
                </label>
                {accountSettings.logo_url && (
                  <button
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(`${API}/settings/account`, { logo_url: null }, {
                          headers: { Authorization: `Bearer ${token}` }
                        });
                        setAccountSettings(prev => ({ ...prev, logo_url: null }));
                        showToast('Logo removed', 'success');
                      } catch (err) {
                        showToast('Failed to remove logo', 'error');
                      }
                    }}
                    className="px-4 py-2 text-red-600 text-sm font-medium hover:bg-red-50 rounded-lg transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">Displayed on your booking page and in the sidebar</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Business Name</label>
              <input
                type="text"
                value={accountSettings.business_name}
                onChange={(e) => setAccountSettings({...accountSettings, business_name: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Email</label>
              <input
                type="email"
                value={accountSettings.email}
                onChange={(e) => setAccountSettings({...accountSettings, email: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Phone</label>
              <input
                type="tel"
                value={accountSettings.phone}
                onChange={(e) => setAccountSettings({...accountSettings, phone: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Website</label>
              <input
                type="url"
                value={accountSettings.website}
                onChange={(e) => setAccountSettings({...accountSettings, website: e.target.value})}
                className="clara-input w-full"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Address</label>
              <textarea
                value={accountSettings.address}
                onChange={(e) => setAccountSettings({...accountSettings, address: e.target.value})}
                className="clara-input w-full"
                rows="3"
              />
            </div>
            <div>
              <label className="block clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Timezone</label>
              <select
                value={accountSettings.timezone}
                onChange={(e) => setAccountSettings({...accountSettings, timezone: e.target.value})}
                className="clara-input w-full"
              >
                <option value="UTC">UTC</option>
                <option value="America/New_York">Eastern Time</option>
                <option value="America/Chicago">Central Time</option>
                <option value="America/Denver">Mountain Time</option>
                <option value="America/Los_Angeles">Pacific Time</option>
              </select>
            </div>
          </div>
          <div className="mt-8">
            <button
              onClick={updateAccountSettings}
              className="clara-btn-primary px-6 py-3"
            >
              Update Account Settings
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Working Hours */}
      {settingsTab === 'hours' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700">
            <h3 className="text-lg font-semibold text-white">Working Hours</h3>
          </div>
          <div className="p-8">
          <div className="space-y-4">
            {Object.entries(workingHours)
              .filter(([day]) => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(day))
              .map(([day, hours]) => (
              <div key={day} className="flex items-center space-x-4">
                <div className="w-24">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={hours.enabled}
                      onChange={(e) => setWorkingHours({
                        ...workingHours,
                        [day]: { ...hours, enabled: e.target.checked }
                      })}
                      className="mr-2"
                    />
                    <span className="clara-body-sm font-medium" style={{ textTransform: 'capitalize', color: 'var(--clara-secondary)' }}>
                      {day}
                    </span>
                  </label>
                </div>
                {hours.enabled && (
                  <>
                    <input
                      type="time"
                      value={hours.start}
                      onChange={(e) => setWorkingHours({
                        ...workingHours,
                        [day]: { ...hours, start: e.target.value }
                      })}
                      className="clara-input"
                    />
                    <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>to</span>
                    <input
                      type="time"
                      value={hours.end}
                      onChange={(e) => setWorkingHours({
                        ...workingHours,
                        [day]: { ...hours, end: e.target.value }
                      })}
                      className="clara-input"
                    />
                  </>
                )}
                {!hours.enabled && (
                  <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>Closed</span>
                )}
              </div>
            ))}
          </div>
          <div className="mt-8">
            <button
              onClick={updateWorkingHours}
              className="clara-btn-primary px-6 py-3"
            >
              Update Working Hours
            </button>
          </div>
          </div>
        </div>
      )}

      {/* Team Members */}
      {/* Team section moved to standalone tab */}
      {false && settingsTab === 'team' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Team Members</h3>
            <button
              onClick={() => setShowAddTeamMember(true)}
              className="clara-btn-primary px-4 py-2"
            >
              Add Team Member
            </button>
          </div>
          <div className="p-8 space-y-6">
            {teamMembers.map((member) => (
              <div key={member.id} className="p-6 border rounded-lg" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="clara-body font-semibold" style={{ color: 'var(--clara-primary)', margin: 0 }}>{member.name}</h4>
                    <p className="clara-body-sm">{member.email}</p>
                    <p className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>{member.role}</p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditTeamMember(member)}
                      className="clara-btn-primary px-3 py-1 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteTeamMember(member.id)}
                      className="clara-btn-secondary px-3 py-1 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>
                
                {/* Working Hours */}
                <div className="mb-4">
                  <h5 className="clara-body-sm font-medium mb-3" style={{ color: 'var(--clara-secondary)' }}>Working Hours:</h5>
                  <div className="grid grid-cols-7 gap-2 text-xs">
                    {member.working_hours && Object.entries(member.working_hours)
                      .filter(([day]) => ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].includes(day))
                      .map(([day, hours]) => (
                      <div key={day} className="text-center">
                        <div className="font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>
                          {day.substring(0, 3)}
                        </div>
                        <div style={{ color: 'var(--clara-medium-gray)' }}>
                          {hours.enabled ? `${hours.start}-${hours.end}` : 'Off'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Assigned Products */}
                <div className="mb-4">
                  <h5 className="clara-body-sm font-medium mb-2" style={{ color: 'var(--clara-secondary)' }}>Assigned Services:</h5>
                  <div className="flex flex-wrap gap-2">
                    {member.assigned_products && member.assigned_products.length > 0 ? (
                      member.assigned_products.map((productId) => {
                        const product = products.find(p => p.id === productId);
                        return product ? (
                          <span key={productId} className="px-2 py-1 bg-gray-100 rounded text-xs" style={{ color: 'var(--clara-medium-gray)' }}>
                            {product.name}
                          </span>
                        ) : null;
                      })
                    ) : (
                      <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>No services assigned</span>
                    )}
                  </div>
                </div>

                {/* Google Calendar Integration */}
                <div className="mt-4 p-3 bg-blue-50 rounded" style={{ borderColor: 'var(--clara-border)' }}>
                  <div className="flex items-center justify-between">
                    <h5 className="clara-body-sm font-medium mb-1" style={{ color: 'var(--clara-secondary)' }}>Google Calendar:</h5>
                    <div className="flex items-center">
                      {member.calendar_integration && member.calendar_integration.is_active ? (
                        <span className="clara-body-sm" style={{ color: 'var(--clara-success)' }}>
                          Connected ✅ (Real Calendar API)
                        </span>
                      ) : (
                        <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                          Not Connected
                        </span>
                      )}
                      <span className="ml-2 text-xl">📅</span>
                    </div>
                  </div>
                  <div className="mt-2">
                    {member.calendar_integration && member.calendar_integration.is_active ? (
                      <div className="flex space-x-2">
                        <span className="clara-body-sm text-green-600">
                          Connected: {new Date(member.calendar_integration.connected_at).toLocaleDateString()}
                        </span>
                        <button
                          onClick={() => disconnectTeamMemberCalendar(member.id)}
                          className="clara-btn-secondary px-3 py-1 text-sm"
                        >
                          Disconnect
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedMemberForCalendar(member);
                          setShowCalendarModal(true);
                        }}
                        className="clara-btn-primary px-3 py-1 text-sm"
                      >
                        Connect Real Calendar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {teamMembers.length === 0 && (
              <p className="clara-body-sm text-center py-8" style={{ color: 'var(--clara-medium-gray)' }}>No team members yet</p>
            )}
          </div>
        </div>
      )}

      {/* Products & Services */}
      {/* Products section moved to standalone tab */}
      {false && settingsTab === 'products' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Products & Services</h3>
            <button
              onClick={() => setShowAddProduct(true)}
              className="clara-btn-primary px-4 py-2"
            >
              Add Product
            </button>
          </div>
          <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {products.map((product) => (
              <div key={product.id} className="p-4 border rounded-lg" style={{ borderColor: 'var(--clara-border)' }}>
                <div className="flex justify-between items-start mb-2">
                  <h4 className="clara-body font-semibold" style={{ color: 'var(--clara-primary)', margin: 0 }}>{product.name}</h4>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditProduct(product)}
                      className="clara-btn-primary px-2 py-1 text-xs"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteProduct(product.id)}
                      className="text-red-600 hover:text-red-700 text-xs"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                <p className="clara-body-sm mb-2">{product.description}</p>
                
                {/* Pricing Display */}
                <div className="mb-2">
                  {product.pricing_tiers && product.pricing_tiers.length > 0 ? (
                    <div>
                      <span className="clara-body-sm font-medium" style={{ color: 'var(--clara-primary)' }}>
                        Tiered Pricing:
                      </span>
                      <div className="mt-1 space-y-1">
                        {product.pricing_tiers.map((tier, index) => (
                          <div key={index} className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                            {(tier.min_sqft || 0).toLocaleString()}-{(tier.max_sqft || 0).toLocaleString()} sq ft: ${tier.price} ({tier.duration_minutes || 0} min)
                          </div>
                        ))}
                      </div>
                      <div className="clara-body-sm mt-1" style={{ color: 'var(--clara-medium-gray)' }}>
                        Base: ${product.price} ({product.duration_minutes} min)
                      </div>
                    </div>
                  ) : (
                    <span className="clara-body-sm font-medium" style={{ color: 'var(--clara-primary)' }}>
                      ${product.price}
                    </span>
                  )}
                </div>

                <div className="flex justify-between items-center mb-2">
                  {product.pricing_tiers && product.pricing_tiers.length > 0 ? (
                    <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                      Duration varies by square footage
                    </span>
                  ) : (
                    <span className="clara-body-sm" style={{ color: 'var(--clara-medium-gray)' }}>
                      Duration: {product.duration_minutes} min
                    </span>
                  )}
                  <span className="inline-block px-2 py-1 bg-gray-100 rounded text-xs" style={{ color: 'var(--clara-medium-gray)' }}>
                    {product.category}
                  </span>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <div className="md:col-span-2">
                <p className="clara-body-sm text-center py-8" style={{ color: 'var(--clara-medium-gray)' }}>No products or services yet</p>
              </div>
            )}
          </div>
        </div>
      )}


      {/* Admin-Only Scheduling Tab */}
      {hasPermission('settings', 'manage_business') && settingsTab === 'scheduling' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700">
            <h3 className="text-lg font-semibold text-white">Scheduling Settings</h3>
          </div>
          <div className="p-6 space-y-6">
            {/* Client Reschedule/Cancel Settings */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-1">Client Self-Service</h4>
              <p className="text-sm text-gray-500 mb-4">Control when clients can reschedule or cancel appointments from their portal.</p>
              
              <div className="space-y-4">
                {/* Lockout Hours */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Reschedule/Cancel Lockout Window
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min="0"
                      max="168"
                      value={accountSettings.reschedule_cancel_lockout_hours || 24}
                      onChange={(e) => setAccountSettings(prev => ({
                        ...prev,
                        reschedule_cancel_lockout_hours: parseInt(e.target.value) || 0
                      }))}
                      className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-gray-600">hours before appointment</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Clients won't be able to reschedule or cancel within this window. Set to 0 to always allow changes.
                  </p>
                </div>
                
                {/* Allow Reschedule Toggle */}
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Allow Client Rescheduling</p>
                    <p className="text-sm text-gray-500">Clients can change their appointment date/time</p>
                  </div>
                  <button
                    onClick={() => setAccountSettings(prev => ({
                      ...prev,
                      allow_client_reschedule: !(prev.allow_client_reschedule !== false)
                    }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      accountSettings.allow_client_reschedule !== false ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      accountSettings.allow_client_reschedule !== false ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
                
                {/* Allow Cancel Toggle */}
                <div className="flex items-center justify-between py-3 border-t border-gray-100">
                  <div>
                    <p className="font-medium text-gray-900">Allow Client Cancellation</p>
                    <p className="text-sm text-gray-500">Clients can cancel their appointments</p>
                  </div>
                  <button
                    onClick={() => setAccountSettings(prev => ({
                      ...prev,
                      allow_client_cancel: !(prev.allow_client_cancel !== false)
                    }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      accountSettings.allow_client_cancel !== false ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      accountSettings.allow_client_cancel !== false ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              </div>
              
              <div className="mt-5 pt-4 border-t border-gray-100">
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/settings/account`, {
                        ...accountSettings,
                        reschedule_cancel_lockout_hours: accountSettings.reschedule_cancel_lockout_hours || 24,
                        allow_client_reschedule: accountSettings.allow_client_reschedule !== false,
                        allow_client_cancel: accountSettings.allow_client_cancel !== false
                      });
                      alert('Scheduling settings saved!');
                    } catch (error) {
                      alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  className="clara-btn-primary px-6 py-2"
                >
                  Save Settings
                </button>
              </div>
            </div>
            
            {/* Booking Review Settings */}
            <div className="border border-gray-200 rounded-lg p-5">
              <h4 className="font-semibold text-gray-900 mb-1">Booking Submissions</h4>
              <p className="text-sm text-gray-500 mb-4">Control how new bookings from your public booking page are handled.</p>
              
              <div className="space-y-4">
                {/* Auto-Book Toggle */}
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-gray-900">Auto-Confirm Bookings</p>
                    <p className="text-sm text-gray-500">When enabled, bookings are confirmed automatically. When disabled, they go to a review queue.</p>
                  </div>
                  <button
                    onClick={() => setAccountSettings(prev => ({
                      ...prev,
                      order_form_auto_book: !prev.order_form_auto_book
                    }))}
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                      accountSettings.order_form_auto_book ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    data-testid="admin-auto-book-toggle"
                  >
                    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                      accountSettings.order_form_auto_book ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
                
                {!accountSettings.order_form_auto_book && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                    <strong>Review Queue Mode:</strong> New bookings will appear at the top of the Appointments page for manual approval.
                  </div>
                )}
              </div>
              
              <div className="mt-5 pt-4 border-t border-gray-100">
                <button
                  onClick={async () => {
                    try {
                      await axios.put(`${API}/settings/account`, {
                        ...accountSettings,
                        order_form_auto_book: accountSettings.order_form_auto_book || false
                      });
                      alert('Booking settings saved!');
                    } catch (error) {
                      alert('Failed to save settings: ' + (error.response?.data?.detail || error.message));
                    }
                  }}
                  className="clara-btn-primary px-6 py-2"
                  data-testid="admin-save-order-form-settings"
                >
                  Save Booking Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tasks Tab - Automated Task Rules */}
      {settingsTab === 'tasks' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-white">Automated Task Rules</h3>
            <button
              onClick={() => {
                setShowAddTaskRule(true);
                setEditingTaskRule(null);
                setNewTaskRule({
                  name: '',
                  product_ids: [],
                  team_member_filter: [],
                  task_title: '',
                  task_description: '',
                  assigned_to: '',
                  due_days_after_shoot: 0,
                  exclude_weekends: false,
                  priority: 'medium'
                });
              }}
              className="px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Rule
            </button>
          </div>
          {automatedTaskRules.length === 0 ? (
            <div className="p-8">
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <p className="text-gray-500 text-lg">No automated task rules yet</p>
                <p className="text-gray-400 text-sm mt-2">Create rules to automatically generate tasks when appointments are created</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rule Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Task to Create</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Criteria</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assignment</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {automatedTaskRules.map((rule) => (
                      <tr key={rule.id} className="hover:bg-gray-50">
                        {/* Rule Name Column */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-900">{rule.name}</span>
                            {!rule.is_active && (
                              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>
                            )}
                          </div>
                        </td>
                        
                        {/* Task to Create Column */}
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{rule.task_title}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                              <span className={`px-2 py-0.5 rounded-full ${
                                rule.priority === 'high' ? 'bg-red-100 text-red-700' :
                                rule.priority === 'medium' ? 'bg-yellow-100 text-yellow-400' :
                                'bg-green-100 text-green-700'
                              }`}>
                                {rule.priority}
                              </span>
                              <span>Due: {rule.due_days_after_shoot} days after shoot</span>
                            </div>
                          </div>
                        </td>
                        
                        {/* Criteria Column */}
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {/* Products */}
                            {rule.product_ids && rule.product_ids.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {rule.product_ids.map(prodId => {
                                  const product = products.find(p => p.id === prodId);
                                  return product ? (
                                    <span key={prodId} className="px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded">
                                      {product.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">All products</span>
                            )}
                            
                            {/* Team Members */}
                            {rule.team_member_filter && rule.team_member_filter.length > 0 ? (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {rule.team_member_filter.map(memberId => {
                                  const member = teamMembers.find(m => m.id === memberId);
                                  return member ? (
                                    <span key={memberId} className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                                      {member.name}
                                    </span>
                                  ) : null;
                                })}
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">All team members</span>
                            )}
                          </div>
                        </td>
                        
                        {/* Assignment Column */}
                        <td className="px-6 py-4">
                          {rule.assigned_to ? (
                            <div className="flex items-center gap-2">
                              {(() => {
                                const assignee = teamMembers.find(m => m.id === rule.assigned_to);
                                return assignee ? (
                                  <>
                                    {assignee.avatar && teamMemberAvatarUrls[assignee.id] ? (
                                      <img 
                                        src={teamMemberAvatarUrls[assignee.id]}
                                        alt={assignee.name}
                                        className="w-6 h-6 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs font-semibold">
                                        {assignee.name.charAt(0).toUpperCase()}
                                      </div>
                                    )}
                                    <span className="text-sm text-gray-700">{assignee.name}</span>
                                  </>
                                ) : (
                                  <span className="text-sm text-gray-500">Unknown</span>
                                );
                              })()}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">Unassigned</span>
                          )}
                        </td>
                        
                        {/* Actions Column */}
                        <td className="px-6 py-4 text-right">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => {
                                setEditingTaskRule(rule);
                                setNewTaskRule({
                                  name: rule.name,
                                  product_ids: rule.product_ids || [],
                                  team_member_filter: rule.team_member_filter || [],
                                  task_title: rule.task_title,
                                  task_description: rule.task_description || '',
                                  assigned_to: rule.assigned_to || '',
                                  due_days_after_shoot: rule.due_days_after_shoot,
                                  exclude_weekends: !!rule.exclude_weekends,
                                  priority: rule.priority
                                });
                                setShowAddTaskRule(true);
                              }}
                              className="px-3 py-1.5 text-sm text-blue-400 hover:bg-blue-50 rounded"
                            >
                              Edit
                            </button>
                            <button
                              onClick={async () => {
                                if (window.confirm('Are you sure you want to delete this automation rule?')) {
                                  try {
                                    await axios.delete(`${API}/automated-task-rules/${rule.id}`);
                                    setAutomatedTaskRules(automatedTaskRules.filter(r => r.id !== rule.id));
                                    alert('Rule deleted successfully');
                                  } catch (error) {
                                    console.error('Error deleting rule:', error);
                                    alert('Failed to delete rule');
                                  }
                                }
                              }}
                              className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {/* Coupons Tab */}
      {settingsTab === 'coupons' && (
        <CouponsSettings clients={clients} showToast={showToast} />
      )}

      {/* Tags Tab */}
      {settingsTab === 'tags' && (
        <TagsSettings showToast={showToast} />
      )}

      {/* Import Tab */}
      {/* Integrations Tab */}
      {settingsTab === 'integrations' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700">
            <h3 className="text-lg font-semibold text-white">External Integrations</h3>
          </div>
          <div className="p-8 space-y-8">
            
            {/* Dropbox Integration */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 1.807L0 5.629l6 3.822 6.001-3.822L6 1.807zM18 1.807l-6 3.822 6 3.822 6-3.822-6-3.822zM0 13.274l6 3.822 6.001-3.822L6 9.452 0 13.274zm12 0l6.001 3.822 6-3.822-6-3.822-6.001 3.822zM6 17.097l6.001 3.806 6-3.806L12 13.274 6 17.097z"/>
                    </svg>
                    Dropbox Connection
                  </h4>
                  <p className="text-blue-700 text-sm">
                    {dropboxSettings.is_connected 
                      ? 'Connected - You can import photos from Dropbox' 
                      : 'Connect your Dropbox account to import photos directly'}
                  </p>
                </div>
                {dropboxSettings.is_connected && (
                  <div className="flex items-center gap-2">
                    <span
                      data-testid="dropbox-encrypted-badge"
                      title="OAuth token + app secret are encrypted at rest in the database"
                      className="bg-emerald-100 text-emerald-800 text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1"
                    >
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Encrypted at rest
                    </span>
                    <span className="bg-green-100 text-green-800 text-xs font-medium px-3 py-1 rounded-full">
                      Connected
                    </span>
                  </div>
                )}
              </div>

              {!dropboxSettings.is_connected ? (
                <div className="space-y-4">
                  <p className="text-sm text-gray-600">
                    One-time setup: Get your Dropbox App Key to enable file imports.
                  </p>
                  <ol className="text-sm text-gray-600 list-decimal list-inside space-y-2 bg-blue-50 p-4 rounded-lg">
                    <li>Visit <a href="https://www.dropbox.com/developers/apps/create" target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline font-medium">Dropbox App Console</a></li>
                    <li>Click "Create app"</li>
                    <li>Choose: <strong>Scoped access</strong> → <strong>Full Dropbox</strong></li>
                    <li>Name your app (e.g., "My Media Manager")</li>
                    <li className="font-semibold text-red-600">IMPORTANT: On the Settings tab:
                      <ul className="list-disc ml-6 mt-1 space-y-1 font-normal text-gray-600">
                        <li>Find "Chooser / Saver / Embedder domains"</li>
                        <li>Add your domain (e.g., <code className="bg-gray-200 px-1 rounded">yourdomain.com</code>)</li>
                        <li>For local testing, add: <code className="bg-gray-200 px-1 rounded">localhost</code></li>
                      </ul>
                    </li>
                    <li>On the Permissions tab:
                      <ul className="list-disc ml-6 mt-1 space-y-1">
                        <li>Enable: <strong>files.metadata.read</strong></li>
                        <li>Enable: <strong>files.content.read</strong></li>
                        <li>Click "Submit" to save permissions</li>
                      </ul>
                    </li>
                    <li>Copy the <strong>App key</strong> from Settings tab and paste below</li>
                  </ol>
                  
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dropbox App Key
                    </label>
                    <input
                      type="text"
                      value={dropboxSettings.app_key}
                      onChange={(e) => setDropboxSettings({...dropboxSettings, app_key: e.target.value})}
                      placeholder="Paste your Dropbox App Key here"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-red-600 mt-2 font-medium">
                      ⚠️ Don't skip step 5 (adding domains) or you'll get "App is misconfigured" error!
                    </p>
                  </div>

                  <button
                    onClick={async () => {
                      if (!dropboxSettings.app_key) {
                        alert('Please enter your Dropbox App Key');
                        return;
                      }
                      try {
                        console.log('Saving Dropbox App Key:', dropboxSettings.app_key.substring(0, 10) + '...');
                        const response = await axios.post(`${API}/integrations/dropbox/save-app-key`, {
                          app_key: dropboxSettings.app_key
                        });
                        console.log('Save response:', response.data);
                        setDropboxSettings({...dropboxSettings, is_connected: true});
                        alert('Dropbox configured successfully! You can now import files from Dropbox.');
                      } catch (error) {
                        console.error('Save error:', error);
                        console.error('Error response:', error.response);
                        alert('Failed to save: ' + (error.response?.data?.detail || error.message));
                      }
                    }}
                    disabled={!dropboxSettings.app_key}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
                  >
                    Save App Key
                  </button>
                  
                  <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-xs text-yellow-300 font-medium mb-2">
                      ⚠️ If you get "App is misconfigured" error:
                    </p>
                    <ul className="text-xs text-yellow-400 list-disc ml-4 space-y-1">
                      <li>Go back to your Dropbox App Console</li>
                      <li>Settings tab → Add your domain to "Chooser domains"</li>
                      <li>Permissions tab → Enable file read permissions → Submit</li>
                    </ul>
                  </div>
                  
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-600">
                      <strong>After setup:</strong> Click "Import from Dropbox" in any upload form. You'll just log in with your Dropbox credentials!
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800 font-medium mb-2">
                      ✓ Dropbox is configured!
                    </p>
                    <p className="text-sm text-green-700">
                      When uploading files in any listing, click "Import from Dropbox" to select files from your Dropbox account.
                    </p>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={async () => {
                        if (window.confirm('Remove Dropbox configuration? You can set it up again anytime.')) {
                          try {
                            console.log('Removing Dropbox configuration...');
                            const response = await axios.post(`${API}/integrations/dropbox/remove-app-key`);
                            console.log('Remove response:', response.data);
                            setDropboxSettings({
                              app_key: '',
                              app_secret: '',
                              is_connected: false
                            });
                            alert('Dropbox configuration removed successfully!');
                          } catch (error) {
                            console.error('Remove error:', error);
                            console.error('Error response:', error.response);
                            alert('Failed to remove: ' + (error.response?.data?.detail || error.message));
                          }
                        }
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition text-sm"
                    >
                      Remove Configuration
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Future integrations can be added here */}
            <StripeIntegrationSection />
          </div>
        </div>
      )}

      {settingsTab === 'import' && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 bg-gray-700">
            <h3 className="text-lg font-semibold text-white">Aryeo Data Import</h3>
          </div>
          <div className="p-8 space-y-8">
          
          {/* API Token Management */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex-1">
                <h4 className="font-semibold text-purple-900 mb-2 flex items-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                  </svg>
                  Aryeo API Token
                </h4>
                <p className="text-purple-700 text-sm">
                  {hasAryeoToken 
                    ? `Token configured: ${aryeoToken}`
                    : 'No token configured. Add your Aryeo API token to import data.'
                  }
                </p>
              </div>
              <div className="flex gap-2">
                {hasAryeoToken ? (
                  <>
                    <button
                      onClick={() => {
                        setShowTokenInput(true);
                        setAryeoTokenInput('');
                      }}
                      className="px-4 py-2 text-sm border border-purple-300 bg-white rounded hover:bg-purple-50 font-medium text-purple-700"
                    >
                      Update Token
                    </button>
                    <button
                      onClick={deleteAryeoToken}
                      className="px-4 py-2 text-sm border border-red-300 bg-white rounded hover:bg-red-50 font-medium text-red-600"
                    >
                      Delete
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setShowTokenInput(true)}
                    className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 font-medium"
                  >
                    Add Token
                  </button>
                )}
              </div>
            </div>
            
            {/* Token Input Form */}
            {showTokenInput && (
              <div className="mt-4 p-4 bg-white rounded-lg border border-purple-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste your Aryeo API Token:
                </label>
                <input
                  type="text"
                  value={aryeoTokenInput}
                  onChange={(e) => setAryeoTokenInput(e.target.value)}
                  placeholder="e.g., 137960|q7KcuLg1LDCIu00ou3VMIQQE8GMkgk3O71EK455h"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent mb-3"
                />
                <div className="flex gap-2">
                  <button
                    onClick={saveAryeoToken}
                    disabled={savingToken}
                    className={`px-4 py-2 rounded font-medium ${
                      savingToken
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    {savingToken ? 'Saving...' : 'Save Token'}
                  </button>
                  <button
                    onClick={() => {
                      setShowTokenInput(false);
                      setAryeoTokenInput('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Your token will be securely stored and used for all Aryeo API calls.
                </p>
              </div>
            )}
          </div>
          
          {/* Connection Status */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-semibold text-blue-900 mb-2">Connection Status</h4>
                <p className="text-blue-700 text-sm">
                  {importConnectionStatus === 'connected' 
                    ? 'Successfully connected to Aryeo API'
                    : importConnectionStatus === 'error'
                    ? 'Failed to connect to Aryeo API'
                    : 'Connection not tested'
                  }
                </p>
              </div>
              <button
                onClick={testAryeoConnection}
                disabled={testingConnection}
                className={`px-4 py-2 rounded font-medium ${
                  testingConnection 
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {testingConnection ? 'Testing...' : 'Test Connection'}
              </button>
            </div>
          </div>

          {/* Import Sections */}
          <div className="space-y-8">
            {/* Clients Import */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Import Clients</h4>
                <div className="flex space-x-3">
                  <button
                    onClick={() => previewAryeoData('clients', 1)}
                    disabled={loadingPreview}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {loadingPreview ? 'Loading...' : 'Load Records'}
                  </button>
                  {selectedRecords.clients.length > 0 && (
                    <button
                      onClick={() => importAryeoData('clients')}
                      disabled={importing}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                    >
                      {importing ? 'Importing...' : `Import Selected (${selectedRecords.clients.length})`}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Load client records from Aryeo, select which ones to import, then click import.
              </p>
              
              {/* Records List */}
              {showRecordsList.clients && previewData.clients && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium">Available Records (Showing {previewData.clients.records.length} of {previewData.clients.total_available} total, max 1000)</h5>
                    <div className="space-x-2">
                      <button
                        onClick={() => selectAllRecords('clients')}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => deselectAllRecords('clients')}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {previewData.clients.records.map((record) => (
                      <div
                        key={record.id}
                        className={`flex items-center justify-between p-3 rounded border ${
                          record.already_imported 
                            ? 'bg-green-50 border-green-300' 
                            : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <input
                            type="checkbox"
                            checked={selectedRecords.clients.includes(record.id)}
                            onChange={() => toggleRecordSelection('clients', record.id)}
                            disabled={record.already_imported}
                            className="w-4 h-4"
                          />
                          <div className="flex-1">
                            <div className="font-medium text-gray-900">{record.name}</div>
                            <div className="text-sm text-gray-600">{record.email || 'No email'}</div>
                          </div>
                          {record.already_imported && (
                            <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">
                              ✓ Imported
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Pagination */}
                  {previewData.clients.total_available > importPagination.clients.limit && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Page {importPagination.clients.page} of {Math.ceil(Math.min(previewData.clients.total_available, 1000) / importPagination.clients.limit)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => previewAryeoData('clients', importPagination.clients.page - 1)}
                          disabled={importPagination.clients.page === 1 || loadingPreview}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => previewAryeoData('clients', importPagination.clients.page + 1)}
                          disabled={importPagination.clients.page >= Math.ceil(Math.min(previewData.clients.total_available, 1000) / importPagination.clients.limit) || loadingPreview}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Listings Import */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Import Listings</h4>
                <div className="flex space-x-3">
                  <button
                    onClick={() => previewAryeoData('listings', 1)}
                    disabled={loadingPreview}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {loadingPreview ? 'Loading...' : 'Load Records'}
                  </button>
                  {selectedRecords.listings.length > 0 && (
                    <button
                      onClick={() => importAryeoData('listings')}
                      disabled={importing}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                    >
                      {importing ? 'Importing...' : `Import Selected (${selectedRecords.listings.length})`}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Load property listings from Aryeo, select which ones to import, then click import.
              </p>
              
              {/* Records List */}
              {showRecordsList.listings && previewData.listings && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium">Available Records ({previewData.listings.total_available} total)</h5>
                    <div className="space-x-2">
                      <button
                        onClick={() => selectAllRecords('listings')}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => deselectAllRecords('listings')}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {previewData.listings.records.map((listing) => (
                      <div
                        key={listing.id}
                        className="flex items-center justify-between bg-white p-3 rounded border border-gray-200"
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedRecords.listings.includes(listing.id)}
                            onChange={() => toggleRecordSelection('listings', listing.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {listing.full_address || listing.address?.address_line1 || listing.address?.address_line_1 || listing.address?.street_address || 'No address'}
                            </div>
                            {listing.client_name && (
                              <div className="text-sm text-blue-600 mt-1">
                                Client: {listing.client_name}
                              </div>
                            )}
                            {listing.list_date && (
                              <div className="text-xs text-gray-500 mt-1">
                                Listed: {new Date(listing.list_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Products Import */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Import Products from Storefront</h4>
                <div className="flex space-x-3">
                  <button
                    onClick={() => previewAryeoData('products', 1)}
                    disabled={loadingPreview}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {loadingPreview ? 'Loading...' : 'Load Products'}
                  </button>
                  {selectedRecords.products.length > 0 && (
                    <button
                      onClick={() => importAryeoData('products')}
                      disabled={importing}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                    >
                      {importing ? 'Importing...' : `Import Selected (${selectedRecords.products.length})`}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Load products from the storefront Product API, select which ones to import, then click import.
              </p>
              
              {/* Records List */}
              {showRecordsList.products && previewData.products && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium">Available Products ({previewData.products.total_available} total)</h5>
                    <div className="space-x-2">
                      <button
                        onClick={() => selectAllRecords('products')}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => deselectAllRecords('products')}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {(() => {
                      // Group products by category
                      const grouped = {};
                      previewData.products.records.forEach((product) => {
                        const categoryName = product.categories && product.categories.length > 0 
                          ? product.categories[0].title || product.categories[0].name 
                          : 'Uncategorized';
                        if (!grouped[categoryName]) {
                          grouped[categoryName] = [];
                        }
                        grouped[categoryName].push(product);
                      });
                      
                      return Object.entries(grouped).map(([category, products]) => (
                        <div key={category} className="space-y-2">
                          {/* Category Header */}
                          <div className="text-xs font-bold text-gray-700 uppercase tracking-wider px-2 py-1 bg-gray-100 rounded">
                            {category}
                          </div>
                          
                          {/* Products in this category */}
                          {products.map((product) => {
                            // Get price from variants
                            let price = 'N/A';
                            if (product.variants && product.variants.length > 0) {
                              const priceAmount = product.variants[0].price_amount || product.variants[0].price;
                              if (priceAmount) {
                                price = `$${(priceAmount / 100).toFixed(2)}`;
                              }
                            }
                            
                            return (
                              <div
                                key={product.id}
                                className={`flex items-center justify-between p-3 rounded border ${
                                  product.already_imported 
                                    ? 'bg-green-50 border-green-300' 
                                    : 'bg-white border-gray-200'
                                }`}
                              >
                                <div className="flex items-center space-x-3 flex-1">
                                  <input
                                    type="checkbox"
                                    checked={selectedRecords.products.includes(product.id)}
                                    onChange={() => toggleRecordSelection('products', product.id)}
                                    disabled={product.already_imported}
                                    className="w-4 h-4"
                                  />
                                  <div className="flex-1 flex items-center justify-between">
                                    <span className="font-medium text-gray-900">{product.title || product.name}</span>
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-green-600">{price}</span>
                                      {product.already_imported && (
                                        <span className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded">
                                          ✓ Imported
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}
                  </div>
                  
                  {/* Pagination */}
                  {previewData.products.total_available > importPagination.products.limit && (
                    <div className="flex items-center justify-between mt-4 pt-4 border-t">
                      <div className="text-sm text-gray-600">
                        Page {importPagination.products.page} of {Math.ceil(previewData.products.total_available / importPagination.products.limit)}
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => previewAryeoData('products', importPagination.products.page - 1)}
                          disabled={importPagination.products.page === 1 || loadingPreview}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Previous
                        </button>
                        <button
                          onClick={() => previewAryeoData('products', importPagination.products.page + 1)}
                          disabled={importPagination.products.page >= Math.ceil(previewData.products.total_available / importPagination.products.limit) || loadingPreview}
                          className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Orders Import */}
            <div className="border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900">Import Orders</h4>
                <div className="flex space-x-3">
                  <button
                    onClick={() => previewAryeoData('orders', 100)}
                    disabled={loadingPreview}
                    className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    {loadingPreview ? 'Loading...' : 'Load Records'}
                  </button>
                  {selectedRecords.orders.length > 0 && (
                    <button
                      onClick={() => importAryeoData('orders')}
                      disabled={importing}
                      className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 font-medium"
                    >
                      {importing ? 'Importing...' : `Import Selected (${selectedRecords.orders.length})`}
                    </button>
                  )}
                </div>
              </div>
              <p className="text-gray-600 text-sm mb-4">
                Load order records from Aryeo, select which ones to import, then click import.
              </p>
              
              {/* Records List */}
              {showRecordsList.orders && previewData.orders && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="font-medium">Available Records ({previewData.orders.total_available} total)</h5>
                    <div className="space-x-2">
                      <button
                        onClick={() => selectAllRecords('orders')}
                        className="text-sm text-blue-400 hover:text-blue-300"
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => deselectAllRecords('orders')}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Deselect All
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {previewData.orders.records.map((order) => (
                      <div
                        key={order.id}
                        className="flex items-center justify-between bg-white p-3 rounded border border-gray-200"
                      >
                        <div className="flex items-center space-x-3">
                          <input
                            type="checkbox"
                            checked={selectedRecords.orders.includes(order.id)}
                            onChange={() => toggleRecordSelection('orders', order.id)}
                            className="w-4 h-4"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900 truncate">
                              {order.property_address || order.address?.address_line_1 || order.address?.address_line1 || 'No address'}
                            </div>
                            {order.client_name && (
                              <div className="text-sm text-blue-600 mt-0.5">
                                Client: {order.client_name}
                              </div>
                            )}
                            <div className="text-xs text-gray-500 mt-1">
                              Order #{String(order.id).slice(0, 8)}
                              {order.created_at && (
                                <> · Created: {new Date(order.created_at).toLocaleDateString()}</>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          </div>
        </div>
      )}

    </div>
  );
};

export default SettingsPage;
